import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Calendar, ChevronLeft, ChevronRight, RefreshCw, Settings, Users,
  MapPin, Clock, CheckCircle2, XCircle, AlertTriangle, CalendarDays,
  Eye, RotateCcw, User, Building2, ChevronDown, ChevronUp,
  Loader2, Info, ArrowRight, Globe, Monitor
} from "lucide-react";
import api from "../../lib/api";
import OrdenDetalleModal from "../ordenes/OrdenDetalleModal";

// ============================================================
// CONSTANTES
// ============================================================
const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const DIAS_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const ESTADO_CONFIG = {
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-800 border-amber-300", dot: "bg-amber-400", icon: Clock },
  completada: { label: "Completada", color: "bg-green-100 text-green-800 border-green-300", dot: "bg-green-500", icon: CheckCircle2 },
  no_realizada: { label: "No realizada", color: "bg-red-100 text-red-800 border-red-300", dot: "bg-red-500", icon: XCircle },
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
export default function CalendarioVisitas() {
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [calendario, setCalendario] = useState(null);
  const [clientesMensuales, setClientesMensuales] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [vistaActiva, setVistaActiva] = useState("calendario"); // calendario, lista, clientes
  const [visitaSeleccionada, setVisitaSeleccionada] = useState(null);
  const [showReagendar, setShowReagendar] = useState(false);
  const [nuevaFecha, setNuevaFecha] = useState("");
  const [motivoReagendamiento, setMotivoReagendamiento] = useState("");
  const [showConfig, setShowConfig] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [filtroCliente, setFiltroCliente] = useState("todos");
  const [expandedDay, setExpandedDay] = useState(null);
  const [incluirRemotas, setIncluirRemotas] = useState(false);
  const [equiposHabilitados, setEquiposHabilitados] = useState({});
  const [equiposPorVisita, setEquiposPorVisita] = useState({});
  const [ordenesInfo, setOrdenesInfo] = useState({});
  const [feriados, setFeriados] = useState([]);
  const [ordenParaDetalle, setOrdenParaDetalle] = useState(null);
  const [togglingEquipo, setTogglingEquipo] = useState(null);

  // ============================================================
  // DATA FETCHING
  // ============================================================
  const cargarCalendario = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/calendario-visitas/${anio}/${mes}`);
      setCalendario(data.calendario);
      setClientesMensuales(data.clientes_mensuales || []);
      setTecnicos(data.tecnicos || []);
      setEquiposHabilitados(data.equipos_habilitados || {});
      setEquiposPorVisita(data.equipos_por_visita || {});
      setOrdenesInfo(data.ordenes_info || {});
      setFeriados(data.feriados || []);
      // Leer config de remotas del calendario guardado
      setIncluirRemotas(data.calendario?.configuracion?.incluir_remotas || false);
    } catch (err) {
      console.error("Error cargando calendario:", err);
    } finally {
      setLoading(false);
    }
  }, [anio, mes]);

  useEffect(() => { cargarCalendario(); }, [cargarCalendario]);

  const toggleEquiposCliente = async (clienteId, sucursal) => {
    const key = `${clienteId}::${sucursal || ''}`;
    const nuevoValor = !equiposHabilitados[key];
    setTogglingEquipo(key);
    try {
      const { data } = await api.put('/calendario-visitas/equipos-config', { key, habilitado: nuevoValor });
      setEquiposHabilitados(data.habilitados || {});
      // Recargar para obtener equipos actualizados
      await cargarCalendario();
    } catch (err) {
      console.error('Error toggling equipos:', err);
    } finally {
      setTogglingEquipo(null);
    }
  };

  const regenerarCalendario = async () => {
    if (!window.confirm("¿Regenerar el calendario? Se perderán cambios manuales.")) return;
    setGenerando(true);
    try {
      await api.post("/calendario-visitas/generar", {
        anio, mes, forzar_regenerar: true, incluir_remotas: incluirRemotas
      });
      await cargarCalendario();
    } catch (err) {
      alert("Error regenerando: " + (err.response?.data?.detail || err.message));
    } finally {
      setGenerando(false);
    }
  };

  const sincronizarOrdenes = async () => {
    try {
      const { data } = await api.post("/calendario-visitas/sync-ordenes", { anio, mes });
      alert(`Sincronización: ${data.completadas} completadas, ${data.reagendadas} reagendadas`);
      await cargarCalendario();
    } catch (err) {
      alert("Error sincronizando: " + (err.response?.data?.detail || err.message));
    }
  };

  const reagendarVisita = async () => {
    if (!visitaSeleccionada || !nuevaFecha) return;
    try {
      await api.post(`/calendario-visitas/reagendar/${visitaSeleccionada.id}`, {
        anio, mes, nueva_fecha: nuevaFecha, motivo: motivoReagendamiento || "Reagendada manualmente"
      });
      setShowReagendar(false);
      setVisitaSeleccionada(null);
      setNuevaFecha("");
      setMotivoReagendamiento("");
      await cargarCalendario();
    } catch (err) {
      alert("Error reagendando: " + (err.response?.data?.detail || err.message));
    }
  };

  const cambiarTecnico = async (tecnicoId) => {
    try {
      await api.put("/calendario-visitas/cambiar-tecnico", {
        anio, mes, tecnico_id: tecnicoId
      });
      await cargarCalendario();
    } catch (err) {
      alert("Error cambiando técnico: " + (err.response?.data?.detail || err.message));
    }
  };

  // ============================================================
  // COMPUTED DATA
  // ============================================================
  const visitas = calendario?.visitas || [];
  const resumen = calendario?.resumen || {};

  const visitasFiltradas = useMemo(() => {
    return visitas.filter(v => {
      if (filtroEstado !== "todos" && v.estado !== filtroEstado) return false;
      if (filtroCliente !== "todos" && v.cliente_id !== filtroCliente) return false;
      return true;
    });
  }, [visitas, filtroEstado, filtroCliente]);

  // Agrupar visitas por fecha para la vista de calendario
  const visitasPorDia = useMemo(() => {
    const map = {};
    for (const v of visitasFiltradas) {
      if (!map[v.fecha_programada]) map[v.fecha_programada] = [];
      map[v.fecha_programada].push(v);
    }
    return map;
  }, [visitasFiltradas]);

  // Datos del grid del calendario
  const diasCalendario = useMemo(() => {
    const primerDia = new Date(anio, mes - 1, 1);
    const ultimoDia = new Date(anio, mes, 0);
    const diasEnMes = ultimoDia.getDate();
    let diaSemanaInicio = primerDia.getDay(); // 0=Dom
    // Ajustar para que lunes=0
    diaSemanaInicio = diaSemanaInicio === 0 ? 6 : diaSemanaInicio - 1;

    const dias = [];
    // Días vacíos antes
    for (let i = 0; i < diaSemanaInicio; i++) {
      dias.push({ dia: null, fecha: null });
    }
    // Días del mes
    for (let d = 1; d <= diasEnMes; d++) {
      const fechaStr = `${anio}-${String(mes).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      dias.push({
        dia: d,
        fecha: fechaStr,
        visitas: visitasPorDia[fechaStr] || [],
        esHoy: fechaStr === `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`,
        esQuincena1: d <= 15,
        esFeriado: feriados.includes(fechaStr)
      });
    }
    return dias;
  }, [anio, mes, visitasPorDia, hoy, feriados]);

  // Clientes únicos de las visitas
  const clientesUnicos = useMemo(() => {
    const map = {};
    for (const v of visitas) {
      if (!map[v.cliente_id]) {
        map[v.cliente_id] = v.cliente_nombre;
      }
    }
    return Object.entries(map).map(([id, nombre]) => ({ id, nombre }));
  }, [visitas]);

  // ============================================================
  // NAV HELPERS
  // ============================================================
  const mesAnterior = () => {
    if (mes === 1) { setMes(12); setAnio(a => a - 1); }
    else setMes(m => m - 1);
  };
  const mesSiguiente = () => {
    if (mes === 12) { setMes(1); setAnio(a => a + 1); }
    else setMes(m => m + 1);
  };

  // ============================================================
  // RENDER
  // ============================================================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        <span className="ml-3 text-gray-500">Cargando calendario...</span>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-2 sm:px-4 py-4 space-y-4">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <CalendarDays className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Calendario de Visitas</h1>
            <p className="text-sm text-gray-500">
              Técnico: <span className="font-medium text-emerald-600">{calendario?.tecnico_nombre || "Sin asignar"}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={sincronizarOrdenes} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition">
            <RefreshCw className="w-4 h-4" /> Sincronizar
          </button>
          <button onClick={regenerarCalendario} disabled={generando} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition disabled:opacity-50">
            {generando ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Regenerar
          </button>
          <button onClick={() => setShowConfig(!showConfig)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 transition">
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* CONFIG PANEL */}
      {showConfig && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-3">
          <h3 className="font-semibold text-gray-700 flex items-center gap-2"><Settings className="w-4 h-4" /> Configuración</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Técnico asignado</label>
              <select
                value={calendario?.tecnico_id || ""}
                onChange={(e) => cambiarTecnico(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {tecnicos.map(t => (
                  <option key={t.id || t._id} value={t.id || t._id}>{t.nombre_completo}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <div className={`relative w-10 h-5 rounded-full transition-colors ${incluirRemotas ? 'bg-emerald-500' : 'bg-gray-300'}`}
                  onClick={() => setIncluirRemotas(!incluirRemotas)}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${incluirRemotas ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
                <Globe className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Incluir ubicaciones remotas</span>
              </label>
              <p className="text-xs text-gray-400 ml-12">CDE, Encarnación, Santa Rita, Coronel Oviedo, San Bernardino</p>
              {incluirRemotas !== (calendario?.configuracion?.incluir_remotas || false) && (
                <p className="text-xs text-amber-600 ml-12 font-medium">⚠ Regenere el calendario para aplicar este cambio</p>
              )}
            </div>
          </div>

          {/* Equipos por cliente/sucursal */}
          <div className="border-t pt-3 mt-1">
            <h4 className="text-sm font-semibold text-gray-600 flex items-center gap-2 mb-2">
              <Monitor className="w-4 h-4" /> Mostrar equipos por cliente / sucursal
            </h4>
            <p className="text-xs text-gray-400 mb-3">Activá la visibilidad de equipos para cada cliente y sucursal. Por defecto está desactivado para todos.</p>
            <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
              {clientesMensuales.map(c => {
                const cid = c.id || c._id;
                const sucs = c.sucursales && c.sucursales.length > 0 ? c.sucursales : [{ nombre: "" }];
                return sucs.map(suc => {
                  const key = `${cid}::${suc.nombre || ''}`;
                  const activo = !!equiposHabilitados[key];
                  const eqCount = (equiposPorVisita[key] || []).length;
                  const isToggling = togglingEquipo === key;
                  return (
                    <div key={key} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm text-gray-700 font-medium truncate">{c.nombre}</span>
                        {suc.nombre && (
                          <span className="text-xs text-gray-400 flex items-center gap-0.5 flex-shrink-0">
                            <MapPin className="w-3 h-3" />{suc.nombre}
                          </span>
                        )}
                        {activo && eqCount > 0 && (
                          <span className="text-[10px] bg-teal-100 text-teal-700 rounded-full px-1.5 flex-shrink-0">{eqCount} eq.</span>
                        )}
                      </div>
                      <button
                        onClick={() => toggleEquiposCliente(cid, suc.nombre || '')}
                        disabled={isToggling}
                        className="flex-shrink-0 ml-2"
                      >
                        {isToggling ? (
                          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                        ) : (
                          <div className={`relative w-9 h-5 rounded-full transition-colors ${activo ? 'bg-teal-500' : 'bg-gray-300'}`}>
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${activo ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </div>
                        )}
                      </button>
                    </div>
                  );
                });
              })}
            </div>
          </div>
        </div>
      )}

      {/* RESUMEN CARDS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <ResumenCard label="Total Visitas" value={resumen.total_visitas || 0} icon={Calendar} color="blue" />
        <ResumenCard label="Completadas" value={resumen.completadas || 0} icon={CheckCircle2} color="green" sub={`${resumen.porcentaje_completado || 0}%`} />
        <ResumenCard label="Pendientes" value={resumen.pendientes || 0} icon={Clock} color="amber" />
        <ResumenCard label="No Realizadas" value={resumen.no_realizadas || 0} icon={XCircle} color="red" />
      </div>
      {resumen.ubicaciones_remotas_excluidas > 0 && !calendario?.configuracion?.incluir_remotas && (
        <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-700">
          <Globe className="w-4 h-4 flex-shrink-0" />
          <span>{resumen.ubicaciones_remotas_excluidas} ubicaciones remotas excluidas del calendario (CDE, Encarnación, etc.)</span>
        </div>
      )}

      {/* NAVEGACIÓN MES + VISTAS */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={mesAnterior} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h2 className="text-lg font-bold text-gray-800 min-w-[180px] text-center">
            {MESES[mes - 1]} {anio}
          </h2>
          <button onClick={mesSiguiente} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Filtros */}
          <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-2 py-1.5">
            <option value="todos">Todos los estados</option>
            <option value="pendiente">Pendientes</option>
            <option value="completada">Completadas</option>
            <option value="no_realizada">No realizadas</option>
          </select>
          <select value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-2 py-1.5">
            <option value="todos">Todos los clientes</option>
            {clientesUnicos.map(c => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>

          {/* Tabs de vista */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {[
              { key: "calendario", label: "Calendario", icon: Calendar },
              { key: "lista", label: "Lista", icon: CalendarDays },
              { key: "clientes", label: "Clientes", icon: Users },
            ].map(tab => (
              <button key={tab.key}
                onClick={() => setVistaActiva(tab.key)}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-md transition ${vistaActiva === tab.key ? "bg-white shadow text-emerald-600 font-medium" : "text-gray-500 hover:text-gray-700"}`}>
                <tab.icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* QUINCENAS INDICATOR */}
      <div className="flex gap-3">
        <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-sm">
          <div className="w-3 h-3 bg-emerald-400 rounded-full"></div>
          <span className="text-emerald-700 font-medium">1ª Quincena (1-15)</span>
        </div>
        <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-sm">
          <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
          <span className="text-purple-700 font-medium">2ª Quincena (16+)</span>
        </div>
      </div>

      {/* VISTAS */}
      {vistaActiva === "calendario" && (
        <VistaCalendario
          dias={diasCalendario}
          onSelectVisita={setVisitaSeleccionada}
          expandedDay={expandedDay}
          setExpandedDay={setExpandedDay}
          equiposPorVisita={equiposPorVisita}
          equiposHabilitados={equiposHabilitados}
        />
      )}

      {vistaActiva === "lista" && (
        <VistaLista
          visitas={visitasFiltradas}
          onSelectVisita={setVisitaSeleccionada}
          onReagendar={(v) => { setVisitaSeleccionada(v); setShowReagendar(true); }}
          equiposPorVisita={equiposPorVisita}
          equiposHabilitados={equiposHabilitados}
        />
      )}

      {vistaActiva === "clientes" && (
        <VistaClientes
          visitas={visitas}
          clientesMensuales={clientesMensuales}
        />
      )}

      {/* MODAL DETALLE VISITA */}
      {visitaSeleccionada && !showReagendar && (
        <VisitaDetalleModal
          visita={visitaSeleccionada}
          onClose={() => setVisitaSeleccionada(null)}
          onReagendar={() => setShowReagendar(true)}
          equipos={(equiposPorVisita[`${visitaSeleccionada.cliente_id}::${visitaSeleccionada.sucursal || ''}`] || [])}
          ordenInfo={ordenesInfo[visitaSeleccionada.orden_id] || null}
          onVerOrden={(ordenId) => {
            setVisitaSeleccionada(null);
            setOrdenParaDetalle({ _id: ordenId });
          }}
        />
      )}

      {/* MODAL DETALLE ORDEN (desde calendario) */}
      {ordenParaDetalle && (
        <OrdenDetalleModal
          orden={ordenParaDetalle}
          onClose={() => setOrdenParaDetalle(null)}
          onUpdate={() => {}}
        />
      )}

      {/* MODAL REAGENDAR */}
      {showReagendar && visitaSeleccionada && (
        <ReagendarModal
          visita={visitaSeleccionada}
          nuevaFecha={nuevaFecha}
          setNuevaFecha={setNuevaFecha}
          motivo={motivoReagendamiento}
          setMotivo={setMotivoReagendamiento}
          onConfirm={reagendarVisita}
          onClose={() => { setShowReagendar(false); setVisitaSeleccionada(null); }}
        />
      )}
    </div>
  );
}


// ============================================================
// SUB-COMPONENTES
// ============================================================

function ResumenCard({ label, value, icon: Icon, color, sub }) {
  const colors = {
    blue: "bg-emerald-50 border-emerald-200 text-emerald-600",
    green: "bg-green-50 border-green-200 text-green-600",
    amber: "bg-amber-50 border-amber-200 text-amber-600",
    red: "bg-red-50 border-red-200 text-red-600",
  };
  return (
    <div className={`border rounded-xl p-3 ${colors[color]}`}>
      <div className="flex items-center justify-between">
        <Icon className="w-5 h-5 opacity-60" />
        {sub && <span className="text-xs font-medium opacity-70">{sub}</span>}
      </div>
      <p className="text-2xl font-bold mt-1">{value}</p>
      <p className="text-xs opacity-70">{label}</p>
    </div>
  );
}


function VistaCalendario({ dias, onSelectVisita, expandedDay, setExpandedDay, equiposPorVisita, equiposHabilitados }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header días */}
      <div className="grid grid-cols-7 bg-gray-50 border-b">
        {DIAS_SEMANA.map(d => (
          <div key={d} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase">{d}</div>
        ))}
      </div>

      {/* Grid de días */}
      <div className="grid grid-cols-7">
        {dias.map((d, idx) => (
          <div
            key={idx}
            className={`min-h-[90px] sm:min-h-[110px] border-b border-r p-1 transition ${d.dia ? "hover:bg-gray-50" : "bg-gray-50/30"} ${d.esHoy ? "bg-emerald-50/50 ring-1 ring-inset ring-emerald-300" : ""} ${d.esQuincena1 === false && d.dia ? "bg-purple-50/20" : ""} ${d.esFeriado ? "bg-red-50/60" : ""}`}
            onClick={() => d.dia && d.visitas.length > 0 && setExpandedDay(expandedDay === d.fecha ? null : d.fecha)}
          >
            {d.dia && (
              <>
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-xs font-medium ${d.esHoy ? "bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center" : d.esFeriado ? "text-red-500" : "text-gray-400"}`}>
                    {d.dia}
                  </span>
                  {d.esFeriado && (
                    <span className="text-[9px] bg-red-100 text-red-600 rounded px-1 font-medium">Feriado</span>
                  )}
                  {!d.esFeriado && d.visitas.length > 0 && (
                    <span className="text-[10px] bg-gray-200 text-gray-600 rounded-full px-1.5">{d.visitas.length}</span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {d.visitas.slice(0, expandedDay === d.fecha ? 10 : 3).map(v => {
                    const vKey = `${v.cliente_id}::${v.sucursal || ''}`;
                    const eqCount = (equiposPorVisita[vKey] || []).length;
                    return <VisitaChip key={v.id} visita={v} onClick={(e) => { e.stopPropagation(); onSelectVisita(v); }} equiposCount={equiposHabilitados[vKey] ? eqCount : 0} />;
                  })}
                  {d.visitas.length > 3 && expandedDay !== d.fecha && (
                    <div className="text-[10px] text-gray-400 text-center cursor-pointer">+{d.visitas.length - 3} más</div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function VisitaChip({ visita: v, onClick, equiposCount }) {
  const cfg = ESTADO_CONFIG[v.estado] || ESTADO_CONFIG.pendiente;
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-1 px-1 py-0.5 rounded text-[10px] sm:text-[11px] cursor-pointer border transition hover:shadow-sm ${cfg.color}`}
      title={`${v.cliente_nombre}${v.sucursal ? ` - ${v.sucursal}` : ""}${equiposCount ? ` | ${equiposCount} equipos` : ""} | ${v.hora_inicio}-${v.hora_fin} (${v.duracion_horas}h)`}
    >
      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`}></div>
      <span className="truncate font-medium">{v.cliente_nombre}{v.sucursal ? ` (${v.sucursal})` : ""}</span>
      {equiposCount > 0 && <Monitor className="w-2.5 h-2.5 flex-shrink-0 opacity-60" />}
    </div>
  );
}


function VistaLista({ visitas, onSelectVisita, onReagendar, equiposPorVisita, equiposHabilitados }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 text-gray-600 font-medium">Fecha</th>
              <th className="text-left px-3 py-2 text-gray-600 font-medium">Cliente</th>
              <th className="text-left px-3 py-2 text-gray-600 font-medium hidden sm:table-cell">Sucursal</th>
              <th className="text-left px-3 py-2 text-gray-600 font-medium">Horario</th>
              <th className="text-left px-3 py-2 text-gray-600 font-medium hidden sm:table-cell">Horas</th>
              <th className="text-left px-3 py-2 text-gray-600 font-medium">Q</th>
              <th className="text-left px-3 py-2 text-gray-600 font-medium hidden sm:table-cell">Eq.</th>
              <th className="text-left px-3 py-2 text-gray-600 font-medium">Estado</th>
              <th className="text-left px-3 py-2 text-gray-600 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visitas.map(v => {
              const cfg = ESTADO_CONFIG[v.estado] || ESTADO_CONFIG.pendiente;
              const Icon = cfg.icon;
              const vKey = `${v.cliente_id}::${v.sucursal || ''}`;
              const eqCount = equiposHabilitados[vKey] ? (equiposPorVisita[vKey] || []).length : 0;
              return (
                <tr key={v.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onSelectVisita(v)}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">{formatFecha(v.fecha_programada)}</td>
                  <td className="px-3 py-2">
                    <span className="font-medium text-gray-800">{v.cliente_nombre}</span>
                    {v.sucursal && <span className="sm:hidden text-xs text-gray-400 block">{v.sucursal}</span>}
                    {v.ubicacion_remota && <Globe className="w-3 h-3 text-orange-400 inline ml-1" title="Ubicación remota" />}
                  </td>
                  <td className="px-3 py-2 text-gray-500 hidden sm:table-cell">
                    {v.sucursal || "-"}
                    {v.ubicacion_remota && <Globe className="w-3 h-3 text-orange-400 inline ml-1" title="Remota" />}
                  </td>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{v.hora_inicio} - {v.hora_fin}</td>
                  <td className="px-3 py-2 text-gray-600 hidden sm:table-cell">{v.duracion_horas}h</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block w-5 h-5 text-center text-xs font-bold rounded-full leading-5 ${v.quincena === 1 ? "bg-emerald-100 text-emerald-600" : "bg-purple-100 text-purple-600"}`}>
                      {v.quincena}
                    </span>
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell">
                    {eqCount > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs text-teal-700 bg-teal-50 rounded-full px-1.5 py-0.5">
                        <Monitor className="w-3 h-3" />{eqCount}
                      </span>
                    ) : <span className="text-gray-300">-</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
                      <Icon className="w-3 h-3" /> {cfg.label}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => onSelectVisita(v)} className="p-1 hover:bg-gray-100 rounded" title="Ver detalle">
                        <Eye className="w-4 h-4 text-gray-400" />
                      </button>
                      {v.estado === "pendiente" && (
                        <button onClick={() => onReagendar(v)} className="p-1 hover:bg-amber-50 rounded" title="Reagendar">
                          <RotateCcw className="w-4 h-4 text-amber-500" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {visitas.length === 0 && (
          <div className="py-12 text-center text-gray-400">No hay visitas con los filtros seleccionados</div>
        )}
      </div>
    </div>
  );
}


function VistaClientes({ visitas, clientesMensuales }) {
  // Agrupar por cliente
  const porCliente = useMemo(() => {
    const map = {};
    for (const v of visitas) {
      if (!map[v.cliente_id]) {
        map[v.cliente_id] = {
          id: v.cliente_id,
          nombre: v.cliente_nombre,
          visitas: [],
          completadas: 0,
          pendientes: 0,
          no_realizadas: 0,
          horas_total: 0,
          horas_completadas: 0,
          sucursales: new Set()
        };
      }
      const g = map[v.cliente_id];
      g.visitas.push(v);
      g.horas_total += v.duracion_horas;
      if (v.estado === "completada") { g.completadas++; g.horas_completadas += v.duracion_horas; }
      else if (v.estado === "pendiente") g.pendientes++;
      else if (v.estado === "no_realizada") g.no_realizadas++;
      if (v.sucursal) g.sucursales.add(v.sucursal);
    }

    // Agregar horas contratadas
    for (const cm of clientesMensuales) {
      const cid = cm.id || cm._id;
      if (map[cid]) {
        map[cid].horas_contratadas = cm.horas_contratadas || 0;
      }
    }

    return Object.values(map).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [visitas, clientesMensuales]);

  const [expanded, setExpanded] = useState(null);

  return (
    <div className="space-y-3">
      {porCliente.map(c => {
        const pct = c.visitas.length > 0 ? Math.round(c.completadas / c.visitas.length * 100) : 0;
        const isExp = expanded === c.id;
        return (
          <div key={c.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div
              className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition"
              onClick={() => setExpanded(isExp ? null : c.id)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800">{c.nombre}</h3>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{c.visitas.length} visitas</span>
                    {c.sucursales.size > 0 && <span>• {c.sucursales.size} sucursales</span>}
                    <span>• {c.horas_total.toFixed(1)}h / {c.horas_contratadas || "?"}h contratadas</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Progress bar */}
                <div className="hidden sm:flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="text-xs font-medium text-gray-500">{pct}%</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="bg-green-100 text-green-700 rounded-full px-2 py-0.5 text-xs font-medium">{c.completadas}</span>
                  <span className="bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 text-xs font-medium">{c.pendientes}</span>
                  {c.no_realizadas > 0 && <span className="bg-red-100 text-red-700 rounded-full px-2 py-0.5 text-xs font-medium">{c.no_realizadas}</span>}
                </div>
                {isExp ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </div>

            {isExp && (
              <div className="border-t divide-y">
                {c.visitas.sort((a, b) => a.fecha_programada.localeCompare(b.fecha_programada)).map(v => {
                  const cfg = ESTADO_CONFIG[v.estado] || ESTADO_CONFIG.pendiente;
                  const Icon = cfg.icon;
                  return (
                    <div key={v.id} className="flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full ${cfg.dot}`}></span>
                        <span className="text-gray-700 font-medium">{formatFecha(v.fecha_programada)}</span>
                        {v.sucursal && (
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <MapPin className="w-3 h-3" /> {v.sucursal}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-gray-500 text-xs">{v.hora_inicio} - {v.hora_fin} ({v.duracion_horas}h)</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${cfg.color}`}>
                          <Icon className="w-3 h-3" /> {cfg.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function VisitaDetalleModal({ visita: v, onClose, onReagendar, equipos, ordenInfo, onVerOrden }) {
  const cfg = ESTADO_CONFIG[v.estado] || ESTADO_CONFIG.pendiente;
  const Icon = cfg.icon;

  const tieneCoords = v.coordenadas_lat && v.coordenadas_lng;
  const googleMapsUrl = tieneCoords
    ? `https://www.google.com/maps?q=${v.coordenadas_lat},${v.coordenadas_lng}`
    : null;

  // Construir texto de ubicación
  const direccionTexto = [v.direccion, v.ciudad].filter(Boolean).join(", ");

  // Formatear fecha de la orden vinculada
  const formatFechaOrden = (f) => {
    if (!f) return "";
    try {
      const d = f.includes("T") ? f.split("T")[0] : f;
      const parts = d.split("-");
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    } catch { return f; }
  };

  const handleVerOrden = () => {
    if (!v.orden_id || !onVerOrden) return;
    onVerOrden(v.orden_id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-emerald-500" />
            Detalle de Visita
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition">
            <XCircle className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Estado y Quincena */}
          <div className="flex items-center justify-between">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${cfg.color}`}>
              <Icon className="w-4 h-4" /> {cfg.label}
            </span>
            <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${v.quincena === 1 ? "bg-emerald-100 text-emerald-600" : "bg-purple-100 text-purple-600"}`}>
              Quincena {v.quincena}
            </span>
          </div>

          {/* Info principal */}
          <div className="space-y-3">
            <InfoRow icon={Building2} label="Cliente" value={v.cliente_nombre} />
            {v.sucursal && <InfoRow icon={Building2} label="Sucursal" value={v.sucursal} />}
            {v.ubicacion_remota && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-700">
                <Globe className="w-3.5 h-3.5" /> Ubicación remota (fuera del Departamento Central)
              </div>
            )}
            <InfoRow icon={Calendar} label="Fecha" value={formatFecha(v.fecha_programada)} />
            <InfoRow icon={Clock} label="Horario" value={`${v.hora_inicio} - ${v.hora_fin} (${v.duracion_horas}h)`} />

            {/* Ubicación con dirección y Google Maps */}
            {(direccionTexto || tieneCoords) && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-xs text-gray-400">Ubicación</span>
                  {direccionTexto && (
                    <p className="text-sm text-gray-700">{direccionTexto}</p>
                  )}
                  {!direccionTexto && tieneCoords && (
                    <p className="text-sm text-gray-500 italic">Sin dirección registrada</p>
                  )}
                  {googleMapsUrl && (
                    <a
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition"
                    >
                      <MapPin className="w-3.5 h-3.5" />
                      Ver en Google Maps
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Orden vinculada - con info detallada */}
            {v.orden_id && (
              <div className="flex items-start gap-3">
                <Eye className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-xs text-gray-400">Orden vinculada</span>
                  {ordenInfo ? (
                    <div
                      onClick={handleVerOrden}
                      className="mt-1 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer hover:bg-emerald-100 transition group"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-emerald-700 group-hover:underline">
                          {ordenInfo.numero_orden}
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-emerald-400 group-hover:text-emerald-600 transition" />
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          {formatFechaOrden(ordenInfo.fecha)}
                        </span>
                        {ordenInfo.nombre_tecnico && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3 text-gray-400" />
                            {ordenInfo.nombre_tecnico}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p
                      onClick={handleVerOrden}
                      className="text-sm text-blue-600 hover:underline cursor-pointer mt-0.5"
                    >
                      {v.orden_id}
                    </p>
                  )}
                </div>
              </div>
            )}

            {v.reagendada_de && (
              <InfoRow icon={RotateCcw} label="Reagendada desde" value={formatFecha(v.reagendada_de)} />
            )}
            {v.notas && <InfoRow icon={Info} label="Notas" value={v.notas} />}
          </div>

          {/* Equipos del cliente/sucursal */}
          {equipos && equipos.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-1">
                <Monitor className="w-4 h-4 text-teal-500" /> Equipos ({equipos.length})
              </h4>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {equipos.map(eq => (
                  <div key={eq._id} className="flex items-center justify-between px-2.5 py-1.5 bg-teal-50 border border-teal-100 rounded-lg text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <Monitor className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="font-medium text-gray-700 block truncate">{eq.nombre || eq.tipo_equipo || 'Sin nombre'}</span>
                        <span className="text-gray-400">{[eq.marca, eq.modelo].filter(Boolean).join(' ')}</span>
                      </div>
                    </div>
                    <span className={`flex-shrink-0 ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      eq.estado === 'activo' ? 'bg-green-100 text-green-700' :
                      eq.estado === 'en_reparacion' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-500'
                    }`}>{eq.estado}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Historial de reagendamientos */}
          {v.historial_reagendamientos && v.historial_reagendamientos.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4 text-amber-500" /> Historial de Reagendamientos
              </h4>
              <div className="space-y-1">
                {v.historial_reagendamientos.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded p-2">
                    <span className="font-medium">{formatFecha(h.fecha_original)}</span>
                    <ArrowRight className="w-3 h-3" />
                    <span className="font-medium">{formatFecha(h.reagendada_a)}</span>
                    <span className="text-gray-400">- {h.motivo}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {v.estado === "pendiente" && (
          <div className="p-4 border-t flex justify-end gap-2">
            <button onClick={onReagendar}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition">
              <RotateCcw className="w-4 h-4" /> Reagendar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


function ReagendarModal({ visita, nuevaFecha, setNuevaFecha, motivo, setMotivo, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-amber-500" />
            Reagendar Visita
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {visita.cliente_nombre}{visita.sucursal ? ` - ${visita.sucursal}` : ""} | {formatFecha(visita.fecha_programada)}
          </p>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nueva fecha</label>
            <input type="date" value={nuevaFecha} onChange={e => setNuevaFecha(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo (opcional)</label>
            <textarea value={motivo} onChange={e => setMotivo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2}
              placeholder="Ej: Cliente no disponible" />
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition">Cancelar</button>
          <button onClick={onConfirm} disabled={!nuevaFecha}
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-50">
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}


function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
      <div>
        <span className="text-xs text-gray-400">{label}</span>
        <p className="text-sm text-gray-700">{value}</p>
      </div>
    </div>
  );
}


function formatFecha(str) {
  if (!str) return "-";
  try {
    const parts = str.split("-");
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  } catch {
    return str;
  }
}
